/**
 * 面包屑导航组件 - 从路由配置动态生成
 */
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { Breadcrumb } from 'antd'
import { DownOutlined, UpOutlined } from '@ant-design/icons'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '@/store/authStore'
import { useLanguage } from '@/hooks/useLanguage'
import { buildBreadcrumbs, findRouteChain } from './breadcrumbUtils'
import styles from './Breadcrumb.module.less'

const BREADCRUMB_ACTION_SLOT_ID = 'app-breadcrumb-actions-slot'
const BREADCRUMB_ACTION_CONTENT_SLOT_ID = 'app-breadcrumb-actions-content-slot'

const BreadcrumbNav: React.FC = () => {
  const location = useLocation()
  const { t } = useTranslation('common')
  const getAllRoutes = useAuthStore(state => state.getAllRoutes)
  const { isEnUS } = useLanguage()
  const actionsSlotRef = useRef<HTMLDivElement | null>(null)
  const [hasActions, setHasActions] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)
  const [userExpanded, setUserExpanded] = useState(false)

  const routeChain = useMemo(() => {
    const pathname = location.pathname

    if (pathname === '/') {
      return null
    }

    const routes = getAllRoutes()
    return findRouteChain(routes, pathname)
  }, [location.pathname, getAllRoutes])

  const currentRoute = routeChain?.[routeChain.length - 1]
  const hideBreadcrumbTitle = Boolean(currentRoute?.hideBreadcrumbTitle)
  const hideGlobalBreadcrumb = Boolean(currentRoute?.hideGlobalBreadcrumb)

  // 从路由配置动态生成面包屑
  const breadcrumbs = useMemo(() => {
    const pathname = location.pathname

    // 首页特殊处理
    if (pathname === '/') {
      return [{ path: '/', label: t('page.home') }]
    }

    if (routeChain && routeChain.length > 0) {
      return buildBreadcrumbs(routeChain, t('page.home'), isEnUS)
    }

    // 未找到匹配路由，返回默认面包屑
    return [{ path: '/', label: t('page.home') }, { path: pathname, label: t('page.unknownPage') }]
  }, [location.pathname, routeChain, t, isEnUS])

  useLayoutEffect(() => {
    const actionsSlot = actionsSlotRef.current
    if (!actionsSlot) {
      setHasActions(false)
      return
    }

    const updateHasActions = () => {
      setHasActions(actionsSlot.childElementCount > 0)
    }

    updateHasActions()

    const observer = new MutationObserver(updateHasActions)
    observer.observe(actionsSlot, { childList: true, subtree: true })

    return () => {
      observer.disconnect()
    }
  }, [location.pathname])

  useEffect(() => {
    const scrollContainer = document.querySelector<HTMLElement>('[data-app-scroll-container="true"]')
    if (!scrollContainer) {
      setIsScrolled(false)
      return
    }

    const collapseThreshold = 0
    let ticking = false

    const updateScrollState = () => {
      ticking = false
      const nextIsScrolled = hasActions && scrollContainer.scrollTop > collapseThreshold
      setIsScrolled((current) => (current === nextIsScrolled ? current : nextIsScrolled))
    }

    const handleScroll = () => {
      if (ticking) return
      ticking = true
      window.requestAnimationFrame(updateScrollState)
    }

    updateScrollState()
    scrollContainer.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll)
    }
  }, [hasActions, location.pathname])

  useEffect(() => {
    setUserExpanded(false)
  }, [location.pathname])

  // 只有一个面包屑时不显示（首页）
  if (hideGlobalBreadcrumb || breadcrumbs.length <= 1) {
    return null
  }

  const currentItem = breadcrumbs[breadcrumbs.length - 1]
  const collapsed = hasActions && (!userExpanded || isScrolled)
  const shouldShowBreadcrumbTitle = !hideBreadcrumbTitle || hasActions
  const toggleLabel = collapsed ? t('expandModal') : t('action.collapsePanel')

  return (
    <div
      className={[
        styles.breadcrumb,
        collapsed ? styles.breadcrumbCollapsed : '',
        !shouldShowBreadcrumbTitle ? styles.breadcrumbTitleHidden : '',
      ].filter(Boolean).join(' ')}
    >
      <div className={styles.breadcrumbShell}>
        <div className={styles.trailRow}>
          <Breadcrumb
            separator={<span className={styles.separator}>/</span>}
            items={breadcrumbs.map((item, index) => ({
              key: item.path,
              title: item.label,
              className: index === breadcrumbs.length - 1 ? styles.lastItem : '',
            }))}
          />
        </div>

        <div className={styles.breadcrumbBar}>
          {shouldShowBreadcrumbTitle ? (
            <div className={styles.breadcrumbMain}>
              <h1 className={styles.currentTitle}>{currentItem.label}</h1>
            </div>
          ) : null}
          <div
            id={BREADCRUMB_ACTION_SLOT_ID}
            className={styles.actionsSlot}
            data-collapsed={collapsed ? 'true' : 'false'}
            data-has-actions={hasActions ? 'true' : 'false'}
          >
            <div
              id={BREADCRUMB_ACTION_CONTENT_SLOT_ID}
              ref={actionsSlotRef}
              className={styles.actionsContent}
              data-collapsed={collapsed ? 'true' : 'false'}
            />
            {hasActions ? (
              <button
                type="button"
                className={styles.actionsToggle}
                aria-label={toggleLabel}
                title={toggleLabel}
                onClick={() => {
                  if (collapsed) {
                    setUserExpanded(true)
                    const scrollContainer = document.querySelector<HTMLElement>('[data-app-scroll-container="true"]')
                    if (scrollContainer && scrollContainer.scrollTop > 0) {
                      scrollContainer.scrollTo({ top: 0, behavior: 'smooth' })
                    }
                    return
                  }

                  setUserExpanded(false)
                }}
              >
                {collapsed ? <DownOutlined /> : <UpOutlined />}
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  )
}

export default BreadcrumbNav
